import React, {Fragment, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Link} from 'react-router-dom';
import {Modal, ModalBody, ModalHeader} from 'reactstrap';
import {Bell, CaretDown, CaretRight, ChartLineUp, PencilSimple, PlugsConnected, Trash} from '@phosphor-icons/react';
import {APIClient} from '../../../utils/APIClient';
import {API_URL} from '../../../settings/local_settings';
import {ChannelLogo} from '../../channels/ChannelLogo';
import {CHANNEL_INFO_BY_CHANNEL} from '../../channels/channels';
import {
    METHOD_INCLUDE_ADGROUP_ON_CONDITION,
    METHOD_INCLUDE_ALL_CAMPAIGNS,
    METHOD_INCLUDE_CAMPAIGN_ON_CONDITION,
    METHOD_SELECT_ADGROUPS_FROM_LIST
} from '../../business_units/accounts/AccountCampaignsConfiguration';
import {BusinessUnitAccounts} from '../../business_units/accounts/BusinessUnitAccounts';
import {getMetricName} from '../../dashboard/columns';
import {FORMAT_METRIC_VALUE} from '../../dashboard/metrics';
import AdAccountBudgetWithDetails from '../../agency/AdAccountBudgetWithDetails';
import {EditClientAdAccountModalButton} from '../ad_accounts/EditClientAdAccountModalButton';
import {DAILY_LIMIT_TYPE_DATA, getLocalizedShortText} from '../../../daily_limits';

function formatBudget(currency, amount) {
    if (!amount && amount !== 0) return null;
    const num = parseFloat(amount);
    if (isNaN(num)) return null;
    const prefix = currency === 'USD' ? 'USD ' : currency === 'ARS' ? 'ARS ' : (currency || '') + ' ';
    if (num >= 1000) {
        return prefix + (num / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    }
    return prefix + num.toLocaleString();
}

function getFirstGoal(bu) {
    const goals = bu.goals_data;
    if (!goals || !goals.business_unit_goals || goals.business_unit_goals.length === 0) return null;
    const sorted = [...goals.business_unit_goals].sort((a, b) => (a.priority || 99) - (b.priority || 99));
    return sorted[0];
}

function getAccountName(config) {
    return config?.name || config?.ad_account_name || config?.account_name || config?.ad_account_account_id || config?.account_id || '';
}

function getAccountId(config) {
    return config?.ad_account_account_id || config?.account_id || config?.ad_account_id || '';
}

function getConfigId(config) {
    return config?.id || config?.bucc_id || config?.campaigns_configuration_id || config?.campaign_configuration_id;
}

function normalizeAccountsResponse(data) {
    if (!data) return [];
    if (Array.isArray(data)) return data;
    return Object.keys(data).map((key) => ({
        id: key,
        ...data[key],
    }));
}

function findClientAdAccount(config, clientAdAccounts) {
    const candidates = clientAdAccounts || [];
    return candidates.find(account => {
        const configClientId = config?.agency_client_ad_account_id || config?.client_ad_account_id;
        if (configClientId && String(account.id) === String(configClientId)) return true;
        if (config?.ad_account_id && String(account.ad_account_id) === String(config.ad_account_id)) return true;
        if (config?.ad_account_account_id && String(account.account_id) === String(config.ad_account_account_id)) return true;
        if (config?.account_id && String(account.account_id) === String(config.account_id)) return true;
        return false;
    });
}

function buildEditableClientAdAccount(accountData, clientAccount) {
    if (clientAccount) return clientAccount;

    const clientAdAccountId = accountData?.agency_client_ad_account_id || accountData?.client_ad_account_id;
    if (!clientAdAccountId) return null;

    return {
        ...accountData,
        id: clientAdAccountId,
        name: getAccountName(accountData),
        account_id: getAccountId(accountData),
    };
}

function formatMoney(currency, value) {
    if (value === null || value === undefined || value === '') return null;
    const amount = Number(value);
    if (Number.isNaN(amount)) return null;
    return `${currency || ''} $${amount.toLocaleString('en-US')}`.trim();
}

function getCampaignsData(config) {
    if (config.all_not_active_campaigns) {
        return [...(config.all_active_account_campaigns || config.all_account_campaigns || []), ...config.all_not_active_campaigns];
    }
    return [
        ...(config.all_active_account_campaigns || config.all_account_campaigns || []),
        ...(config.some_selected_not_active_campaigns_data || [])
    ];
}

function getAdgroupsData(config) {
    if (config.all_not_active_adgroups) {
        return [...(config.all_active_account_adgroups || config.all_account_adgroups || []), ...config.all_not_active_adgroups];
    }
    return [
        ...(config.all_active_account_adgroups || config.all_account_adgroups || []),
        ...(config.some_selected_not_active_adgroups_data || [])
    ];
}

function getItemName(item) {
    if (item.campaign_name === undefined) return item.name;
    return `${item.campaign_name} - ${item.name}`;
}

function getConfiguredCampaignItems(config, t) {
    const method = config.selection_method;

    if (method === METHOD_INCLUDE_ALL_CAMPAIGNS) {
        return (config.all_active_account_campaigns || config.all_account_campaigns || [])
            .filter(item => item.is_active !== false)
            .map(item => ({id: item.id, name: item.name}));
    }

    if (method === METHOD_SELECT_ADGROUPS_FROM_LIST) {
        const selectedAdgroups = (config.selected_adgroups || []).map(id => id.toString());
        return getAdgroupsData(config)
            .filter(item => selectedAdgroups.includes(item.id.toString()))
            .map(item => ({id: item.id, name: getItemName(item)}));
    }

    if (method === METHOD_INCLUDE_CAMPAIGN_ON_CONDITION || method === METHOD_INCLUDE_ADGROUP_ON_CONDITION) {
        const items = [];
        if (config.include_containing) {
            items.push({id: 'include-condition', name: `${t('account_setup.condition_include')}: ${config.include_containing}`});
        }
        if (config.exclude_containing) {
            items.push({id: 'exclude-condition', name: `${t('account_setup.condition_exclude')}: ${config.exclude_containing}`});
        }
        return items;
    }

    const selectedCampaigns = (config.selected_campaigns || []).map(id => id.toString());
    return getCampaignsData(config)
        .filter(item => selectedCampaigns.includes(item.id.toString()))
        .map(item => ({id: item.id, name: item.name}));
}

function getConfiguredActionItems(config, t) {
    const selectedActions = config.selected_actions || [];
    const channelInfo = CHANNEL_INFO_BY_CHANNEL[config.channel];

    if (selectedActions.length === 0 && channelInfo?.hasIncludeAllConversionsSetting) {
        return [{id: 'all-primary-conversions', name: t('actions_multi_select.all_primary_conversions')}];
    }

    return selectedActions.map(actionId => {
        const action = (config.all_actions || []).find(item => {
            const itemId = item.action_id != null ? item.action_id.toString() : null;
            return itemId === actionId.toString();
        });
        return {
            id: actionId,
            name: action ? action.description : actionId
        };
    });
}

export function BUSummaryRow({bu, clientId, clientName, clientAdAccounts, defaultOpen, showAccountControls, onAccountsChanged, onControlLineDeleted}) {
    const {t, i18n} = useTranslation();
    const [isOpen, setIsOpen] = useState(defaultOpen === true);
    const [accounts, setAccounts] = useState(null);
    const [loadingAccounts, setLoadingAccounts] = useState(false);
    const [deletingConfigId, setDeletingConfigId] = useState(null);
    const [deletingControlLine, setDeletingControlLine] = useState(false);
    const [expandedAccountIds, setExpandedAccountIds] = useState({});
    const [editingConfigId, setEditingConfigId] = useState(null);

    const channels = bu.bucc_channels || [];
    const budgetText = formatBudget(bu.budget_currency, bu.allocated_budget);
    const goal = getFirstGoal(bu);
    const baseUrl = `/business-unit/${bu.id}`;

    const fetchAccounts = (force = false) => {
        if (!force && (accounts !== null || loadingAccounts)) return;
        setLoadingAccounts(true);
        new APIClient(`${API_URL}business-unit/${bu.id}/campaigns-configurations`).call('GET').then((response) => {
            if (response && response.status === 'OK') {
                setAccounts(normalizeAccountsResponse(response.data));
            } else {
                setAccounts([]);
            }
            setLoadingAccounts(false);
        });
    };

    const toggleAccounts = () => {
        const nextOpen = !isOpen;
        setIsOpen(nextOpen);
        if (nextOpen) fetchAccounts();
    };

    const handleActionClick = (event) => {
        event.stopPropagation();
    };

    const handleDailyLimitEdited = () => {
        fetchAccounts(true);
        if (onAccountsChanged) onAccountsChanged();
    };

    const toggleAccountDetails = (accountKey) => {
        setExpandedAccountIds(prev => ({
            ...prev,
            [accountKey]: !prev[accountKey],
        }));
    };

    const openCampaignOnboarding = (event, configId) => {
        event.stopPropagation();
        setEditingConfigId(configId);
    };

    const closeCampaignOnboarding = () => {
        setEditingConfigId(null);
    };

    const handleCampaignOnboardingComplete = () => {
        setEditingConfigId(null);
        fetchAccounts(true);
        if (onAccountsChanged) onAccountsChanged();
    };

    const handleDeleteAccount = (account) => {
        const configId = getConfigId(account);
        if (!configId) return;
        const confirmed = window.confirm(t('control_central.bu_delete_account_confirm', {account: getAccountName(account)}));
        if (!confirmed) return;

        setDeletingConfigId(configId);
        new APIClient(`${API_URL}campaigns-configurations/${configId}/delete`).call('GET').then((response) => {
            if (response && response.status === 'OK') {
                setAccounts((prev) => (prev || []).filter(item => getConfigId(item) !== configId));
                if (onAccountsChanged) onAccountsChanged();
            } else {
                alert(response?.error_message || t('control_central.bu_delete_account_error'));
            }
            setDeletingConfigId(null);
        });
    };

    const handleDeleteControlLine = () => {
        const confirmed = window.confirm(t('control_central.bu_delete_confirm', {name: bu.name}));
        if (!confirmed) return;

        setDeletingControlLine(true);
        new APIClient(`${API_URL}business-unit/${bu.id}/delete`).call('GET').then((response) => {
            if (response && response.status === 'OK') {
                if (onControlLineDeleted) onControlLineDeleted(bu.id);
            } else {
                alert(response?.error_message || t('control_central.bu_delete_error'));
            }
            setDeletingControlLine(false);
        });
    };

    useEffect(() => {
        if (defaultOpen === true) {
            fetchAccounts();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultOpen]);

    return (
        <Fragment>
            <tr
                className={`bu-summary-row${isOpen ? ' -open' : ''}`}
                onClick={toggleAccounts}
                role="button"
                tabIndex={0}
                onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault();
                        toggleAccounts();
                    }
                }}
            >
                <td>
                    <div className="bu-summary-name">
                        <button
                            type="button"
                            className="bu-summary-expand-button"
                            onClick={(event) => {
                                event.stopPropagation();
                                toggleAccounts();
                            }}
                            aria-expanded={isOpen}
                            title={isOpen ? t('control_central.bu_collapse_accounts') : t('control_central.bu_expand_accounts')}
                            aria-label={isOpen ? t('control_central.bu_collapse_accounts') : t('control_central.bu_expand_accounts')}
                        >
                            {isOpen ? <CaretDown size={16} weight="bold"/> : <CaretRight size={16} weight="bold"/>}
                        </button>
                        <div className="bu-summary-channels">
                            {channels.map((ch, i) => (
                                <ChannelLogo key={i} channel={ch} height={14} width={14} hspace={0} vspace={0}/>
                            ))}
                        </div>
                        <span className="bu-summary-name-text">{bu.name}</span>
                    </div>
                </td>
                <td>
                    {budgetText ? (
                        <span className="bu-summary-budget">{budgetText}</span>
                    ) : (
                        <span className="bu-summary-no-budget">{t('control_central.bu_no_budget')}</span>
                    )}
                </td>
                <td>
                    {goal ? (
                        <span className="bu-summary-goal">
                            {getMetricName(t, goal.metric_name)} = {FORMAT_METRIC_VALUE(goal.metric_name, goal.value)}
                        </span>
                    ) : (
                        <span className="bu-summary-no-goal">&mdash;</span>
                    )}
                </td>
                <td>
                    <div className="bu-summary-row-actions" onClick={handleActionClick} onKeyDown={handleActionClick}>
                        <Link className="bu-summary-icon-link" to={`${baseUrl}/`} title={t('control_central.bu_action_edit')} aria-label={t('control_central.bu_action_edit')}>
                            <PencilSimple size={16} weight="bold"/>
                        </Link>
                        <Link className="bu-summary-icon-link" to={`${baseUrl}/alerts-configuration`} title={t('control_central.bu_action_alerts')} aria-label={t('control_central.bu_action_alerts')}>
                            <Bell size={16} weight="bold"/>
                        </Link>
                        <Link className="bu-summary-icon-link" to={`${baseUrl}/budget-history`} title={t('control_central.bu_action_stats')} aria-label={t('control_central.bu_action_stats')}>
                            <ChartLineUp size={16} weight="bold"/>
                        </Link>
                        <Link className="bu-summary-icon-link" to={`${baseUrl}/accounts`} title={t('control_central.bu_action_accounts')} aria-label={t('control_central.bu_action_accounts')}>
                            <PlugsConnected size={16} weight="bold"/>
                        </Link>
                        <button
                            type="button"
                            className="bu-summary-icon-link -danger"
                            onClick={handleDeleteControlLine}
                            disabled={deletingControlLine}
                            title={t('control_central.bu_action_delete')}
                            aria-label={t('control_central.bu_action_delete')}
                        >
                            <Trash size={16} weight="bold"/>
                        </button>
                    </div>
                </td>
            </tr>
            {isOpen && (
                <tr className="bu-summary-accounts-row">
                    <td colSpan={4}>
                        {loadingAccounts ? (
                            <div className="bu-summary-accounts-message">{t('control_central.bu_loading_accounts')}</div>
                        ) : accounts && accounts.length > 0 ? (
                            <div className={`bu-summary-accounts-list${showAccountControls ? ' -detailed' : ''}`}>
                                {accounts.map((account) => {
                                    const clientAccount = findClientAdAccount(account, clientAdAccounts);
                                    const accountData = {...account, ...(clientAccount || {})};
                                    const editableClientAccount = buildEditableClientAdAccount(accountData, clientAccount);
                                    const accountName = getAccountName(accountData);
                                    const accountId = getAccountId(accountData);
                                    const channelName = CHANNEL_INFO_BY_CHANNEL[accountData.channel]?.Spanish || accountData.channel;
                                    const dailyBudget = formatMoney(accountData.currency_code, accountData.daily_budget);
                                    const hasDailyLimit = accountData.daily_limit_type && accountData.daily_limit_currency && accountData.daily_limit_value;
                                    const dailyLimit = hasDailyLimit
                                        ? `${getLocalizedShortText(DAILY_LIMIT_TYPE_DATA[accountData.daily_limit_type], i18n.language)} · ${formatMoney(accountData.daily_limit_currency, accountData.daily_limit_value)}`
                                        : t('control_central.bu_no_daily_limit');
                                    const configId = getConfigId(account);
                                    const accountKey = (configId || account.id || accountName || accountId).toString();
                                    const accountIsOpen = expandedAccountIds[accountKey] === true;
                                    const campaignItems = getConfiguredCampaignItems(account, t);
                                    const actionItems = getConfiguredActionItems(account, t);
                                    return (
                                        <div
                                            key={accountKey}
                                            className={`bu-summary-account-item${accountIsOpen ? ' -open' : ''}`}
                                            onClick={() => toggleAccountDetails(accountKey)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(event) => {
                                                if (event.key === 'Enter' || event.key === ' ') {
                                                    event.preventDefault();
                                                    toggleAccountDetails(accountKey);
                                                }
                                            }}
                                        >
                                            <div className="bu-summary-account-main">
                                                <button
                                                    type="button"
                                                    className="bu-summary-account-expand"
                                                    onClick={(event) => {
                                                        event.stopPropagation();
                                                        toggleAccountDetails(accountKey);
                                                    }}
                                                    aria-expanded={accountIsOpen}
                                                    title={accountIsOpen ? t('control_central.account_collapse_details') : t('control_central.account_expand_details')}
                                                    aria-label={accountIsOpen ? t('control_central.account_collapse_details') : t('control_central.account_expand_details')}
                                                >
                                                    {accountIsOpen ? <CaretDown size={14} weight="bold"/> : <CaretRight size={14} weight="bold"/>}
                                                </button>
                                                <ChannelLogo channel={accountData.channel} height={18} width={18} hspace={0} vspace={0}/>
                                                <div className="bu-summary-account-copy">
                                                    <span>{accountName || channelName}</span>
                                                    <small>{channelName}{accountId ? ` · ID ${accountId}` : ''}</small>
                                                </div>
                                            </div>
                                            {showAccountControls && (
                                                <>
                                                    <div className="bu-summary-account-budget">
                                                        <strong>{t('control_central.bu_platform_daily_budget')}</strong>
                                                        {dailyBudget && accountData.ad_account_id ? (
                                                            <AdAccountBudgetWithDetails
                                                                currency={accountData.currency_code}
                                                                dailyBudget={accountData.daily_budget}
                                                                channelHasBudgets={accountData.channel_has_campaigns_with_budgets}
                                                                dailyBudgetUpdatedOn={accountData.last_campaigns_sync}
                                                                campaignsLastUpdate={accountData.campaigns_last_update}
                                                                hasAccess={accountData.has_access}
                                                                hasInternalProblem={accountData.has_internal_problem}
                                                                maxCampaignDailyBudget={accountData.max_campaign_daily_budget}
                                                                adAccountId={accountData.ad_account_id}
                                                            />
                                                        ) : (
                                                            <span>{dailyBudget || t('control_central.bu_without_data')}</span>
                                                        )}
                                                    </div>
                                                    <div className="bu-summary-account-limit">
                                                        <strong>{t('control_central.bu_daily_limit')}</strong>
                                                        <div className="bu-summary-account-limit-value">
                                                            <span>{dailyLimit}</span>
                                                        </div>
                                                    </div>
                                                    {editableClientAccount && (
                                                        <EditClientAdAccountModalButton
                                                            buttonClassName="bu-summary-account-action -budget"
                                                            iconName="dollar-sign"
                                                            title={t('control_central.bu_daily_limit')}
                                                            aria-label={t('control_central.bu_daily_limit')}
                                                            onClientAdAccountEdited={handleDailyLimitEdited}
                                                            clientAdAccount={editableClientAccount}
                                                            clientId={clientId}
                                                            clientName={clientName}
                                                        />
                                                    )}
                                                    <button
                                                        type="button"
                                                        className="bu-summary-account-delete"
                                                        onClick={(event) => {
                                                            event.stopPropagation();
                                                            handleDeleteAccount(account);
                                                        }}
                                                        disabled={deletingConfigId === configId}
                                                        title={t('control_central.bu_delete_account')}
                                                        aria-label={t('control_central.bu_delete_account')}
                                                    >
                                                        <Trash size={16} weight="bold"/>
                                                    </button>
                                                </>
                                            )}
                                            {accountIsOpen && (
                                                <div className="bu-summary-account-details" onClick={(event) => event.stopPropagation()}>
                                                    <div className="bu-summary-account-detail-block">
                                                        <strong>{t('control_central.account_campaigns_title')}</strong>
                                                        {campaignItems.length > 0 ? (
                                                            <div className="bu-summary-account-pill-list">
                                                                {campaignItems.map(item => (
                                                                    <span key={item.id} className="bu-summary-account-pill">{item.name}</span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p>{t('control_central.account_no_campaigns_configured')}</p>
                                                        )}
                                                    </div>
                                                    <div className="bu-summary-account-detail-block">
                                                        <strong>{t('control_central.account_conversions_title')}</strong>
                                                        {actionItems.length > 0 ? (
                                                            <div className="bu-summary-account-pill-list">
                                                                {actionItems.map(item => (
                                                                    <span key={item.id} className="bu-summary-account-pill">{item.name}</span>
                                                                ))}
                                                            </div>
                                                        ) : (
                                                            <p>{t('control_central.account_no_conversions_configured')}</p>
                                                        )}
                                                    </div>
                                                    <div className="bu-summary-account-details-action">
                                                        <button
                                                            type="button"
                                                            className="bu-summary-account-edit-campaigns"
                                                            onClick={(event) => openCampaignOnboarding(event, configId)}
                                                            disabled={!configId}
                                                            title={t('control_central.account_edit_campaign_onboarding')}
                                                            aria-label={t('control_central.account_edit_campaign_onboarding')}
                                                        >
                                                            {t('control_central.account_edit_campaigns')}
                                                        </button>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="bu-summary-accounts-message">{t('control_central.bu_no_accounts')}</div>
                        )}
                    </td>
                </tr>
            )}
            <Modal isOpen={!!editingConfigId} toggle={closeCampaignOnboarding} className="control-central-campaign-onboarding-modal">
                <ModalHeader toggle={closeCampaignOnboarding}>
                    {t('control_central.account_details_modal_title')}
                </ModalHeader>
                <ModalBody>
                    <div className="modal-section">
                        <BusinessUnitAccounts
                            embedded={true}
                            businessUnitId={bu.id}
                            initialEditConfigId={editingConfigId}
                            onBack={closeCampaignOnboarding}
                            onComplete={handleCampaignOnboardingComplete}
                            backText={t('ga4_property.cancel')}
                            finishText={t('client.save_button')}
                            emptyText={t('control_central.bu_no_accounts')}
                        />
                    </div>
                </ModalBody>
            </Modal>
        </Fragment>
    );
}
